import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ArrowUpRight } from "lucide-react";
import { NAV_SERVICES, COMPANY } from "../lib/site";
import { ClientPortalLink } from "./ClientPortalLink";

const Logo = ({ onDark }) => (
  <Link to="/" data-testid="header-logo" className="group flex items-center gap-2.5" aria-label="MJG Promotions home">
    <span className="flex h-9 w-9 items-center justify-center bg-brand-red font-extrabold text-white transition-transform duration-300 group-hover:rotate-[-6deg]">
      M
    </span>
    <span className={`text-lg font-extrabold tracking-tight ${onDark ? "text-white" : "text-brand-black"}`}>
      MJG<span className="text-brand-red">.</span>
      <span className="ml-1 font-mono text-[10px] font-medium uppercase tracking-[0.2em] opacity-60">Promotions</span>
    </span>
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMegaOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinkClass = ({ isActive }) =>
    `text-sm font-semibold transition-colors ${
      isActive ? "text-brand-red" : "text-white/85 hover:text-white"
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
        scrolled ? "border-white/10 bg-brand-black/95 backdrop-blur-md" : "border-white/5 bg-brand-black"
      }`}
      data-testid="site-header"
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:text-brand-black"
      >
        Skip to content
      </a>
      <div className={`mx-auto flex max-w-7xl items-center justify-between px-5 transition-all duration-300 ${scrolled ? "h-16" : "h-20"}`}>
        <Logo onDark />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          <NavLink to="/" className={navLinkClass} data-testid="nav-home" end>Home</NavLink>

          <div
            className="relative"
            onMouseEnter={() => setMegaOpen(true)}
            onMouseLeave={() => setMegaOpen(false)}
          >
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-semibold text-white/85 transition-colors hover:text-white"
              aria-expanded={megaOpen}
              aria-haspopup="true"
              data-testid="nav-services-trigger"
              onClick={() => setMegaOpen((v) => !v)}
            >
              Services
              <ChevronDown className={`h-4 w-4 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
            </button>

            {megaOpen && (
              <div
                className="absolute left-1/2 top-full w-[640px] -translate-x-1/2 pt-4"
                data-testid="services-mega-menu"
              >
                <div className="grid grid-cols-2 gap-1 border border-brand-line bg-white p-3 shadow-2xl">
                  {NAV_SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={s.slug}
                      data-testid={`mega-link${s.slug}`}
                      className="group flex items-start gap-3 p-3 transition-colors hover:bg-brand-warm"
                    >
                      <s.icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" aria-hidden="true" />
                      <span>
                        <span className="block text-sm font-bold text-brand-black">{s.title}</span>
                        <span className="mt-0.5 block text-xs leading-snug text-brand-gray">{s.short}</span>
                      </span>
                    </Link>
                  ))}
                  <div className="col-span-2 mt-1 flex items-center justify-between border-t border-brand-line bg-brand-black p-4">
                    <span className="text-sm text-white/80">
                      <span className="font-bold text-white">Cloud-Based Portal</span> — inventory & ordering for clients
                    </span>
                    <ClientPortalLink
                      testId="mega-portal-link"
                      showIcon
                      className="inline-flex items-center text-sm font-semibold text-white underline decoration-brand-red decoration-2 underline-offset-4"
                    >
                      Access
                    </ClientPortalLink>
                  </div>
                </div>
              </div>
            )}
          </div>

          <NavLink to="/about" className={navLinkClass} data-testid="nav-about">About</NavLink>
          <NavLink to="/contact" className={navLinkClass} data-testid="nav-contact">Contact</NavLink>
        </nav>

        {/* Right utilities */}
        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={COMPANY.phoneHref}
            data-testid="header-phone"
            className="flex items-center gap-1.5 text-sm font-semibold text-white/85 transition-colors hover:text-white"
          >
            <Phone className="h-4 w-4 text-brand-red" aria-hidden="true" />
            {COMPANY.phone}
          </a>
          <ClientPortalLink
            testId="header-portal-button"
            showIcon
            className="inline-flex items-center border border-white/25 px-4 py-2 text-sm font-semibold text-white transition-colors hover:border-white hover:bg-white/5"
          >
            Client Portal
          </ClientPortalLink>
          <Link
            to="/contact"
            data-testid="header-start-project"
            className="inline-flex items-center gap-1.5 bg-brand-red px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-redhover"
          >
            Start a Project <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className="text-white lg:hidden"
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="border-t border-white/10 bg-brand-black lg:hidden" data-testid="mobile-menu">
          <nav className="mx-auto max-w-7xl px-5 py-5" aria-label="Mobile">
            <Link to="/" className="block py-2 text-base font-semibold text-white" data-testid="m-nav-home">Home</Link>
            <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-white/40">Services</p>
            {NAV_SERVICES.map((s) => (
              <Link key={s.slug} to={s.slug} data-testid={`m-nav${s.slug}`} className="flex items-center gap-2 py-2 text-white/85">
                <s.icon className="h-4 w-4 text-brand-red" aria-hidden="true" /> {s.title}
              </Link>
            ))}
            <div className="mt-3 border-t border-white/10 pt-3">
              <Link to="/about" className="block py-2 text-base font-semibold text-white" data-testid="m-nav-about">About</Link>
              <Link to="/contact" className="block py-2 text-base font-semibold text-white" data-testid="m-nav-contact">Contact</Link>
            </div>
            <div className="mt-4 flex flex-col gap-3">
              <ClientPortalLink testId="m-portal-button" showIcon className="inline-flex items-center justify-center border border-white/25 px-4 py-3 text-sm font-semibold text-white">
                Client Portal
              </ClientPortalLink>
              <Link to="/contact" data-testid="m-start-project" className="inline-flex items-center justify-center gap-1.5 bg-brand-red px-5 py-3 text-sm font-bold text-white">
                Start a Project <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a href={COMPANY.phoneHref} className="inline-flex items-center justify-center gap-1.5 py-2 text-sm font-semibold text-white/85">
                <Phone className="h-4 w-4 text-brand-red" /> {COMPANY.phone}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
